import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AnnotationHistoryTable } from '../AnnotationHistoryTable/AnnotationHistoryTable';
import { AnnotationPagination } from '../AnnotationPagination/AnnotationPagination';
import { AnnotationHistoryError } from '../AnnotationHistoryError/AnnotationHistoryError';
import annotationHistoryService from '@/core/services/AnnotationHistoryService';
import { AnnotationHistoryItem } from '@/core/services/interfaces/AnnotationHistoryInterfaces';
import './AnnotationHistoryModal.scss';

const BdsIcon = 'bds-icon' as any;
const ITEMS_PER_PAGE = 5;

export interface AnnotationHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AnnotationHistoryModal: React.FC<AnnotationHistoryModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [allData, setAllData] = useState<AnnotationHistoryItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isOpening, setIsOpening] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const totalPages = Math.ceil(allData.length / ITEMS_PER_PAGE) || 1;

  const fetchData = async () => {
    setCurrentPage(1);
    setIsLoading(true);
    setHasError(false);
    try {
      const result = await annotationHistoryService.getFilterAndBulkAnnotations();
      setAllData(result.annotations ?? []);
    } catch {
      setHasError(true);
      setAllData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setIsOpening(false);
      return;
    }

    setIsClosing(false);
    const timer = setTimeout(() => setIsOpening(true), 10);
    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200);
  }, [onClose]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (e.target === overlayRef.current) {
        handleClose();
      }
    };

    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  if (!isOpen) return null;

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const displayData = allData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  
  const isVisible = isOpening && !isClosing;

  return (
    <div className={`history-drawer-overlay ${isVisible ? 'history-drawer-overlay--visible' : ''}`} ref={overlayRef}>
      <div className={`history-drawer ${isVisible ? 'history-drawer--visible' : ''}`}>
        <div className="history-drawer__header">
          <h2 className="history-drawer__title">{t('sortBy.submenu.annotationHistory.modalTitle')}</h2>
          <button className="history-drawer__close" onClick={handleClose} type="button">
            <BdsIcon name="close" theme="outline" />
          </button>
        </div>

        <div className="history-drawer__content">
          {!isLoading && hasError && (
            <AnnotationHistoryError onRetry={fetchData} />
          )}
          {!isLoading && !hasError && (
            <AnnotationHistoryTable data={displayData} />
          )}
        </div>

        {!hasError && (
          <div className="history-drawer__footer">
            <AnnotationPagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};